/**
 * Sekme şeridinin düzen kuralları — Chrome/Safari ile aynı değişmezler.
 *
 * Şerit rastgele bir dizi değil, iki kuralı her zaman sağlayan bir dizi:
 *   1. Sabitlenmiş sekmeler en başta durur (ve gruba ait olamaz).
 *   2. Bir grubun üyeleri bitişiktir — araya grupsuz sekme giremez.
 *
 * Her mutasyondan sonra `normalizeTabs` çağrılır; böylece sürükleme, gruba taşıma veya
 * sabitleme gibi işlemlerin her biri kuralı ayrı ayrı korumak zorunda kalmaz. Kuralı tek
 * yerde tutmanın nedeni: aksi halde "gruba taşı" + "sabitle" gibi kombinasyonlarda şerit
 * sessizce bozuluyor ve hata ancak ekranda görülüyor.
 */
import type { TabGroup, TabModel } from '../model/types';

/** Şeridin çizim birimi: tek bir sekme ya da bir grup bloğu. */
export type StripSegment =
  { kind: 'tab'; tab: TabModel } | { kind: 'group'; group: TabGroup; tabs: TabModel[] };

export function normalizeTabs(tabs: TabModel[]): TabModel[] {
  // Sabitlenen sekme gruptan çıkar: Chrome'da sabit alan grupların solunda ayrı bir bölge.
  const pinned = tabs.filter((t) => t.pinned).map((t) => (t.groupId ? { ...t, groupId: null } : t));
  const rest = tabs.filter((t) => !t.pinned);

  const out: TabModel[] = [];
  const placed = new Set<string>();
  for (const tab of rest) {
    if (placed.has(tab.tabId)) continue;
    if (!tab.groupId) {
      out.push(tab);
      continue;
    }
    // Grubun tüm üyeleri ilk üyenin bulunduğu yere toplanır.
    for (const member of rest) {
      if (member.groupId === tab.groupId && !placed.has(member.tabId)) {
        out.push(member);
        placed.add(member.tabId);
      }
    }
  }
  return [...pinned, ...out];
}

/** Üyesi kalmayan gruplar silinir — Chrome'da son sekmesi çıkan grup kaybolur. */
export function pruneGroups(tabs: TabModel[], groups: TabGroup[]): TabGroup[] {
  const live = new Set(tabs.map((t) => t.groupId).filter(Boolean));
  return groups.filter((g) => live.has(g.groupId));
}

/**
 * Normalize edilmiş şeridi çizim birimlerine böler. Katlanmış grubun üyeleri döner ama
 * çizmek çağıranın işi — sayaç (grup çipinde "3") için üye listesi gerekiyor.
 */
export function tabStripSegments(tabs: TabModel[], groups: TabGroup[]): StripSegment[] {
  const byId = new Map(groups.map((g) => [g.groupId, g]));
  const out: StripSegment[] = [];
  for (const tab of tabs) {
    const group = tab.groupId ? byId.get(tab.groupId) : undefined;
    if (!group) {
      out.push({ kind: 'tab', tab });
      continue;
    }
    const last = out[out.length - 1];
    if (last?.kind === 'group' && last.group.groupId === group.groupId) last.tabs.push(tab);
    else out.push({ kind: 'group', group, tabs: [tab] });
  }
  return out;
}

/**
 * Katlanan grupta aktif sekme varsa odak dışarı taşınır (Chrome'un davranışı): önce grubun
 * sağındaki, yoksa solundaki sekme, o da yoksa ana sayfa.
 */
export function activeAfterCollapse(
  tabs: TabModel[],
  groupId: string,
  activeTabId: string | null,
): string | null {
  const active = tabs.find((t) => t.tabId === activeTabId);
  if (!active || active.groupId !== groupId) return activeTabId;
  const outside = tabs.filter((t) => t.groupId !== groupId);
  const idx = tabs.findIndex((t) => t.tabId === activeTabId);
  const right = tabs.slice(idx + 1).find((t) => t.groupId !== groupId);
  if (right) return right.tabId;
  const left = [...tabs.slice(0, idx)].reverse().find((t) => t.groupId !== groupId);
  return left?.tabId ?? outside[0]?.tabId ?? null;
}

export type TabSortKey = 'caption' | 'created' | 'recent';

/**
 * Sekmeleri sıralar. Sabitler ve gruplar yerinde kalır — Chrome'da da sıralama grupları
 * dağıtmaz; grup içi sıra ve grupların şeritteki yeri korunur, sıralama grup içinde ve
 * grupsuz sekmeler arasında çalışır.
 */
export function sortTabs(tabs: TabModel[], key: TabSortKey, label: (t: TabModel) => string): TabModel[] {
  const cmp = (a: TabModel, b: TabModel) => {
    if (key === 'caption') return label(a).localeCompare(label(b), 'tr');
    if (key === 'created') return a.createdAt - b.createdAt;
    return b.lastActiveAt - a.lastActiveAt;
  };
  const pinned = tabs.filter((t) => t.pinned);
  const rest = tabs.filter((t) => !t.pinned);

  // Grup blokları yerinde kalsın diye önce bloklara ayrılır, her blok kendi içinde sıralanır.
  const blocks: { groupId: string | null; items: TabModel[] }[] = [];
  for (const tab of rest) {
    const gid = tab.groupId ?? null;
    const last = blocks[blocks.length - 1];
    if (last && last.groupId === gid && gid !== null) last.items.push(tab);
    else blocks.push({ groupId: gid, items: [tab] });
  }
  // Ardışık grupsuz sekmeler tek bir blok gibi sıralanır.
  const merged: { groupId: string | null; items: TabModel[] }[] = [];
  for (const b of blocks) {
    const last = merged[merged.length - 1];
    if (b.groupId === null && last?.groupId === null) last.items.push(...b.items);
    else merged.push(b);
  }
  return [...pinned.slice().sort(cmp), ...merged.flatMap((b) => b.items.slice().sort(cmp))];
}

const COLOR_CYCLE: TabGroup['color'][] = [
  'blue',
  'red',
  'yellow',
  'green',
  'pink',
  'purple',
  'cyan',
  'orange',
  'grey',
];

/** Yeni grup için kullanılmamış ilk rengi seçer; hepsi doluysa baştan döner. */
export function nextGroupColor(groups: TabGroup[]): TabGroup['color'] {
  const used = new Set(groups.map((g) => g.color));
  return COLOR_CYCLE.find((c) => !used.has(c)) ?? COLOR_CYCLE[groups.length % COLOR_CYCLE.length]!;
}
